import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion'; // Using Framer Motion for modern animations
import { 
  MapPin, Calendar, ArrowRight, 
  Clock, CheckCircle, Shield, Star,
  Twitter, Facebook, Linkedin, Instagram,
  Activity, Users, Zap
} from 'lucide-react';

const Home = () => {

  // Mock Data for the Preview Section (Styled exactly like Marketplace)
  const previewLeads = [
    {
      id: 1,
      category: 'Commercial Cleaning',
      urgency: 'high',
      city: 'Chicago',
      state: 'IL',
      date: '2025-10-24', // Future date
      facility_size: '25,000 sq ft',
      price: '299'
    },
    {
      id: 2,
      category: 'Real Estate',
      urgency: 'medium',
      city: 'Miami',
      state: 'FL',
      date: '2025-10-28',
      facility_size: 'Office Building',
      price: '499'
    },
    {
      id: 3,
      category: 'Financial Services',
      urgency: 'low',
      city: 'New York',
      state: 'NY',
      date: '2025-11-02',
      facility_size: '200-500 Emp',
      price: '399'
    }
  ];

  return (
    <div className="font-sans antialiased text-gray-800 bg-white font-[Poppins]">
      
      {/* --- MODERN HERO SECTION --- */}
      <div className="relative bg-slate-900 overflow-hidden min-h-[700px] flex items-center">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 w-full h-full">
            <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-600/30 rounded-full blur-[100px] animate-pulse"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-green-500/20 rounded-full blur-[120px]"></div>
            <div className="absolute top-[40%] left-[40%] w-[300px] h-[300px] bg-orange-500/10 rounded-full blur-[80px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
                <span className="flex h-2 w-2 rounded-full bg-green-400 mr-2 animate-pulse"></span>
                <span className="text-sm font-medium text-green-100">Live Marketplace Active</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-[1.1] mb-6">
                We don't just set meetings. <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-green-400">
                  We set Revenue.
                </span>
              </h1>
              
              <p className="text-lg text-slate-300 mb-8 max-w-lg leading-relaxed">
                Revenue-ready appointments with call recordings and verified proofs. No more chasing uncertain leads. Access a pipeline that actually closes.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/marketplace" 
                  className="group flex items-center justify-center bg-[#F39C12] hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 shadow-lg hover:shadow-orange-500/30 transform hover:-translate-y-1"
                >
                  Explore Marketplace
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Link>
                <Link 
                  to="/register" 
                  className="flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/30 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300"
                >
                  Start Posting Leads
                </Link>
              </div>
            </motion.div>

            {/* Right Visual (Abstract Cards) */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
                {/* Floating Card Visual */}
                <div className="relative z-10 bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-3xl shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-500">
                    <div className="flex justify-between items-center mb-6">
                        <div className="h-10 w-10 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle text="white" size={20} />
                        </div>
                        <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-xs font-bold">VERIFIED</span>
                    </div>
                    <div className="space-y-4">
                        <div className="h-4 bg-white/20 rounded w-3/4"></div>
                        <div className="h-4 bg-white/10 rounded w-1/2"></div>
                    </div>
                    <div className="mt-8 pt-6 border-t border-white/10 flex justify-between items-center">
                        <div>
                            <p className="text-xs text-slate-400">Potential Value</p>
                            <p className="text-2xl font-bold text-white">$15,000+</p>
                        </div>
                        <div className="px-4 py-2 bg-blue-600 rounded-lg text-white text-sm font-bold">Claim Lead</div>
                    </div>
                </div>
                
                {/* Background Decor Card */}
                <div className="absolute top-10 -right-10 z-0 w-full h-full bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl opacity-20 transform -rotate-6"></div>
            </motion.div>

          </div>
        </div>
      </div>

      {/* How It Works */}
      <div id="how-it-works" className="py-20 bg-[#F5F6FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              How It Works
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto">
              Our simple 3-step process to get revenue-ready appointments
            </p>
          </div>
          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center hover:-translate-y-2 transition duration-300">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-50 text-[#2E86DE] mb-6">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Setters Post Appointments</h3>
                <p className="text-gray-500 leading-relaxed">
                  Appointment setters post verified appointments with all required details including proof of confirmation.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center hover:-translate-y-2 transition duration-300">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-50 text-[#10AC84] mb-6">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Admin Approves & Lists</h3>
                <p className="text-gray-500 leading-relaxed">
                  Our admin team conducts rigorous quality checks and lists approved appointments in the marketplace.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center hover:-translate-y-2 transition duration-300">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-orange-50 text-[#F39C12] mb-6">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Companies Claim & Purchase</h3>
                <p className="text-gray-500 leading-relaxed">
                  Businesses discover and purchase appointments instantly, with full details revealed immediately after payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- MARKETPLACE PREVIEW SECTION --- */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-12">
                <div>
                    <h2 className="text-3xl font-extrabold text-gray-900">
                        Marketplace Preview
                    </h2>
                    <p className="mt-2 text-gray-500">
                        Recent opportunities added to the platform
                    </p>
                </div>
                <Link to="/marketplace" className="hidden md:flex items-center text-[#2E86DE] font-bold hover:underline">
                    View All <ArrowRight size={16} className="ml-1" />
                </Link>
            </div>

            {/* Grid of Cards - Styled EXACTLY like Marketplace.js */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {previewLeads.map((lead, index) => (
                  <motion.div 
                    key={lead.id} 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    className="bg-white rounded-3xl shadow-lg border border-gray-100 flex flex-col overflow-hidden group relative hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="p-7 flex-1">
                      {/* Header: Category & Urgency */}
                      <div className="flex justify-between items-start mb-6">
                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-[10px] font-extrabold bg-blue-50 text-blue-600 uppercase tracking-wider">
                          {lead.category}
                        </span>
                        {lead.urgency === 'high' && (
                          <span className="flex items-center text-red-500 text-[10px] font-black bg-red-50 px-2 py-1 rounded-lg">
                            <Activity size={12} className="mr-1" /> URGENT
                          </span>
                        )}
                        {lead.urgency === 'medium' && (
                          <span className="flex items-center text-yellow-600 text-[10px] font-black bg-yellow-50 px-2 py-1 rounded-lg">
                            <Zap size={12} className="mr-1" /> MEDIUM
                          </span>
                        )}
                        {lead.urgency === 'low' && (
                          <span className="flex items-center text-gray-500 text-[10px] font-black bg-gray-100 px-2 py-1 rounded-lg">
                            <Clock size={12} className="mr-1" /> LOW
                          </span>
                        )}
                      </div>
                      
                      {/* BLURRED REVEAL INTERFACE */}
                      <div className="relative mb-6">
                        <h3 className="text-2xl font-black text-gray-800 leading-tight filter blur-[6px] opacity-40 transition-all duration-300 group-hover:opacity-100 group-hover:blur-sm">
                          Confidential Client
                        </h3>
                        <div className="absolute inset-0 flex items-center justify-start opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                          <span className="bg-gray-900/90 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-2xl backdrop-blur-md transform translate-y-2 group-hover:translate-y-0 transition-transform">
                            Sign In to Reveal
                          </span>
                        </div>
                      </div>
                      
                      {/* Details */}
                      <div className="space-y-4">
                        <div className="flex items-center text-gray-600 group-hover:text-gray-900 transition-colors">
                          <div className="p-2.5 bg-gray-50 rounded-xl mr-3 group-hover:bg-[#2E86DE] group-hover:text-white transition-colors duration-300">
                            <MapPin size={18} />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 font-bold uppercase">Location</p>
                            <span className="font-semibold text-sm">{lead.city}, {lead.state}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center text-gray-600 group-hover:text-gray-900 transition-colors">
                          <div className="p-2.5 bg-gray-50 rounded-xl mr-3 group-hover:bg-[#2E86DE] group-hover:text-white transition-colors duration-300">
                            <Calendar size={18} />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 font-bold uppercase">Appointment</p>
                            <span className="font-semibold text-sm">
                              {new Date(lead.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center text-gray-600 group-hover:text-gray-900 transition-colors">
                          <div className="p-2.5 bg-gray-50 rounded-xl mr-3 group-hover:bg-[#2E86DE] group-hover:text-white transition-colors duration-300">
                            <Users size={18} />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 font-bold uppercase">Size</p>
                            <span className="font-semibold text-sm">{lead.facility_size}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer Price Action */}
                    <div className="bg-gray-50 px-7 py-5 border-t border-gray-100 flex items-center justify-between group-hover:bg-[#2E86DE]/5 transition-colors duration-500">
                      <div>
                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Price</p>
                        <p className="text-3xl font-black text-[#2E86DE]">${lead.price}</p>
                      </div>
                      
                      <Link 
                        to="/register"
                        className="flex items-center bg-gradient-to-r from-[#2E86DE] to-blue-700 text-white px-6 py-3 rounded-xl text-sm font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        Unlock Lead
                      </Link>
                    </div>
                  </motion.div>
                ))}
            </div>

            <div className="mt-12 text-center md:hidden">
                <Link to="/marketplace" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#2E86DE] hover:bg-blue-700 transition duration-200">
                    View All Leads in Marketplace
                    <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
            </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-[#F5F6FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
                <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                    Why Companies Love Us
                </h2>
                <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto">
                    Focus on closing deals, not finding opportunities
                </p>
            </div>
            <div className="mt-16 grid grid-cols-1 gap-y-10 gap-x-8 md:grid-cols-3">
                <div className="flex flex-col bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center mb-4">
                        <div className="flex-shrink-0 bg-[#2E86DE]/10 text-[#2E86DE] rounded-xl p-3">
                            <Clock size={28} />
                        </div>
                        <h3 className="ml-3 text-lg font-bold text-gray-900">Save Time & Cost</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                        Eliminate expensive lead generation teams and focus resources on what you do best - closing deals.
                    </p>
                </div>
                <div className="flex flex-col bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center mb-4">
                        <div className="flex-shrink-0 bg-[#10AC84]/10 text-[#10AC84] rounded-xl p-3">
                            <CheckCircle size={28} />
                        </div>
                        <h3 className="ml-3 text-lg font-bold text-gray-900">Verified Appointments</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                        Every lead comes with proof of confirmation - email screenshots or call recordings.
                    </p>
                </div>
                <div className="flex flex-col bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center mb-4">
                        <div className="flex-shrink-0 bg-[#F39C12]/10 text-[#F39C12] rounded-xl p-3">
                            <Shield size={28} />
                        </div>
                        <h3 className="ml-3 text-lg font-bold text-gray-900">Risk-Free Guarantee</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                        Full refund if the appointment doesn't show or is fabricated - we take quality seriously.
                    </p>
                </div>
            </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
                <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                    What Our Clients Say
                </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Testimonial 1 */}
                <div className="bg-gray-50 p-8 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center mb-4">
                        <div className="h-12 w-12 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-xl">D</div>
                        <div className="ml-4">
                            <h4 className="text-lg font-bold text-gray-900">David Wilson</h4>
                            <p className="text-[#10AC84] text-sm font-medium">Cleaning Services Inc.</p>
                        </div>
                    </div>
                    <p className="text-gray-600 italic">
                        "RevenueSettersPro has transformed our sales pipeline. We're closing 30% more deals with qualified appointments that actually show up."
                    </p>
                    <div className="mt-4 flex text-yellow-400">
                        {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" />)}
                    </div>
                </div>
                {/* Testimonial 2 */}
                <div className="bg-gray-50 p-8 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center mb-4">
                        <div className="h-12 w-12 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-xl">S</div>
                        <div className="ml-4">
                            <h4 className="text-lg font-bold text-gray-900">Sarah Johnson</h4>
                            <p className="text-[#10AC84] text-sm font-medium">Real Estate Solutions</p>
                        </div>
                    </div>
                    <p className="text-gray-600 italic">
                        "The quality of leads is outstanding. We've expanded to three new markets thanks to the verified appointments from this platform."
                    </p>
                    <div className="mt-4 flex text-yellow-400">
                        {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" />)}
                    </div>
                </div>
                {/* Testimonial 3 */}
                <div className="bg-gray-50 p-8 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center mb-4">
                        <div className="h-12 w-12 rounded-full bg-orange-200 flex items-center justify-center text-orange-700 font-bold text-xl">M</div>
                        <div className="ml-4">
                            <h4 className="text-lg font-bold text-gray-900">Michael Chen</h4>
                            <p className="text-[#10AC84] text-sm font-medium">Financial Advisory Group</p>
                        </div>
                    </div>
                    <p className="text-gray-600 italic">
                        "As a financial advisor, my time is extremely valuable. This platform ensures every minute I spend is with a qualified, interested prospect."
                    </p>
                    <div className="mt-4 flex text-yellow-400">
                        {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" />)}
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-[#2E86DE] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl mb-6">
                Ready to Transform Your Pipeline?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-10">
                Join hundreds of businesses and appointment setters who are already benefiting from our platform.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/register" className="bg-white hover:bg-gray-100 text-[#2E86DE] font-bold py-4 px-10 rounded-xl text-lg transition duration-200 shadow-xl">
                    I'm a Company
                </Link>
                <Link to="/register" className="bg-[#F39C12] hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-xl text-lg transition duration-200 shadow-xl">
                    I'm a Setter
                </Link>
            </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#222F3E] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
                <div className="col-span-1 md:col-span-2">
                    <h3 className="text-2xl font-bold mb-4 flex items-center">
                        RevenueSetters<span className="text-[#F39C12]">Pro</span>
                    </h3>
                    <p className="text-gray-400 leading-relaxed max-w-sm">
                        The premier marketplace for revenue-ready appointments. Connecting businesses with verified leads since 2023.
                    </p>
                    <div className="mt-6 flex space-x-4">
                        <a href="#" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"><Twitter size={18} /></a>
                        <a href="#" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"><Facebook size={18} /></a>
                        <a href="#" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"><Linkedin size={18} /></a>
                        <a href="#" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"><Instagram size={18} /></a>
                    </div>
                </div>
                <div>
                    <h3 className="text-lg font-bold mb-6">Quick Links</h3>
                    <ul className="space-y-4">
                        <li><Link to="/" className="text-gray-400 hover:text-[#F39C12] transition">Home</Link></li>
                        <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#F39C12] transition">How It Works</Link></li>
                        <li><Link to="/register" className="text-gray-400 hover:text-[#F39C12] transition">For Companies</Link></li>
                        <li><Link to="/register" className="text-gray-400 hover:text-[#F39C12] transition">For Setters</Link></li>
                        <li><Link to="/marketplace" className="text-gray-400 hover:text-[#F39C12] transition">Marketplace</Link></li>
                    </ul>
                </div>
                <div>
                    <h3 className="text-lg font-bold mb-6">Legal</h3>
                    <ul className="space-y-4">
                        <li><a href="#" className="text-gray-400 hover:text-[#F39C12] transition">Privacy Policy</a></li>
                        <li><a href="#" className="text-gray-400 hover:text-[#F39C12] transition">Terms & Conditions</a></li>
                        <li><a href="#" className="text-gray-400 hover:text-[#F39C12] transition">Refund Policy</a></li>
                        <li><a href="#" className="text-gray-400 hover:text-[#F39C12] transition">Contact Us</a></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
                <p className="text-gray-500 text-sm">
                    &copy; 2025 RevenueSetters. All rights reserved.
                </p>
                <p className="text-gray-600 text-sm mt-2 md:mt-0">
                    Designed for Growth.
                </p>
            </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;